// client/src/api/problem-details.ts — RFC 9457 helpers (04 §5.11).
//
// UI-001 shipped only what the composition root needs: the retry predicate of §9.1 has to
// tell a transport failure apart from a problem response the server meant. UI-003 adds the
// half `ErrorState` renders — the reader that turns an unknown thrown value into a problem
// document, and the correlation id that makes it a support handle (02 §19).
//
// The FIELD-error surface — a 400's `errors` dictionary mapped onto form fields via `setError` —
// is `apply-problem-details.ts` (D-3111); this module only exposes the dictionary.

/** RFC 9457 problem document, as produced by the API (02 §7.4). */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Per-field validation errors, keyed by property name. */
  errors?: Record<string, string[]>;
  /**
   * `X-Correlation-Id` of the failing request, echoed into the body by the API (02 §19).
   * §5.11 makes it mandatory on every error surface: it is the one string a user can read
   * out to support that finds the exact request in Seq.
   */
  correlationId?: string;
}

function statusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const candidate = error as { status?: unknown; response?: { status?: unknown } };
  if (typeof candidate.status === 'number') return candidate.status;
  if (typeof candidate.response?.status === 'number') return candidate.response.status;
  return undefined;
}

/**
 * True when `error` carries one of `statuses`. Used by the query retry predicate: a 4xx the
 * server deliberately returned will not become truthful by asking again, so it must not be
 * retried — only transport and 5xx failures deserve a second attempt.
 */
export function isProblemStatus(error: unknown, ...statuses: number[]): boolean {
  const status = statusOf(error);
  return status !== undefined && statuses.includes(status);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Reads a problem document out of whatever was thrown.
 *
 * Deliberately total: a rejected fetch, a generated-client error carrying `body`, a plain
 * `Error` and a string all reach `ErrorState`, and none of them may render a blank card. What
 * cannot be read is simply absent from the result, so the caller falls back to `errors.generic`.
 */
export function toProblemDetails(error: unknown): ProblemDetails {
  if (!isRecord(error)) {
    return typeof error === 'string' ? { detail: error } : {};
  }

  // Three shapes reach here and all must read the same. `ApiError` (api/http.ts, and what
  // api/configure.ts turns every generated-client failure into) carries the document under
  // `problem`; a raw generated-client error hangs the parsed body off `body`; a `fetch`
  // rejection has neither and is read as itself. `problem` is checked FIRST: until TEN-004's
  // swap it was not checked at all, so `problemCode()` never matched a code for any `ApiError`
  // and every feature's error table fell through to the server's `title` — invisible while the
  // tests used the same sentence on both sides (D-370).
  const body = isRecord(error.problem)
    ? error.problem
    : isRecord(error.body)
      ? error.body
      : isRecord(error.error)
        ? error.error
        : error;

  const status = typeof body.status === 'number' ? body.status : statusOf(error);
  const detail = stringOrUndefined(body.detail) ?? stringOrUndefined(error.message);

  const errors =
    isRecord(body.errors) &&
    Object.values(body.errors).every(
      (value) => Array.isArray(value) && value.every((entry) => typeof entry === 'string'),
    )
      ? (body.errors as Record<string, string[]>)
      : undefined;

  // `exactOptionalPropertyTypes`: an unreadable field is an ABSENT key, never `undefined`.
  return {
    ...(stringOrUndefined(body.type) !== undefined && { type: stringOrUndefined(body.type)! }),
    ...(stringOrUndefined(body.title) !== undefined && { title: stringOrUndefined(body.title)! }),
    ...(status !== undefined && { status }),
    ...(detail !== undefined && { detail }),
    ...(stringOrUndefined(body.instance) !== undefined && {
      instance: stringOrUndefined(body.instance)!,
    }),
    ...(errors !== undefined && { errors }),
    ...(correlationIdOf(body) !== undefined && { correlationId: correlationIdOf(body)! }),
  };
}

/** The API writes `correlationId`; some middlewares spell it `traceId`. Accept both. */
function correlationIdOf(body: Record<string, unknown>): string | undefined {
  return stringOrUndefined(body.correlationId) ?? stringOrUndefined(body.traceId);
}
